import { SourProvider } from '@plaidev/sour-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Domain as D } from 'auth-common/domain';
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { AuthInfo } from '../../hook/model/authInfo';
import { useLatestOrganizationId } from '../../hook/Project/latestOrganization';
import { useLatestProjectId } from '../../hook/Project/latestProject';
import { AccountProvider, useAccount } from '../../provider/AccountProvider';
import { AuthProvider, AuthState, useAuthContext } from '../../provider/AuthProvider';
import { OrganizationProvider, useOrganization } from '../../provider/OrganizationProvider';
import { ProjectProvider } from '../../provider/ProjectProvider';
import { route } from '../../router';
import { buildLoginPathWithRedirect } from '../../utils/auth';
import {
  isMfaRequiredAndUnsatisfied,
  isMfaRequiredError,
  redirectForSsoRequiredError,
  redirectToMfaEnrollment,
} from '../../utils/authRequirements';
import { GlobalNaviV2 } from '../GlobalNaviV2';
import type { NavigationProps } from '../Navigation/types';
import { Main, Wrapper } from './Layout';
import { NoAccessibleProduct } from './NoAccessibleProduct';
import { ProjectIpGuard } from './ProjectIpGuard';

export namespace ProductWrapper {
  export type Props = {
    productKey: D.Object.Product.Key;
    navigation: Pick<NavigationProps, 'title' | 'subHeader' | 'globalNaviV2'> & Partial<Pick<NavigationProps, 'menu'>>;
    queryClient?: QueryClient | undefined;
    initialProjectId?: string | undefined;
    // 認証なしで描画するパス。該当すると Authenticator / ProjectSelector / IpGuard を
    // 素通りして children を返す。Tauri の desk WebView などでログイン画面を一切見せたくない
    // ランディングページ用途を想定。
    publicPaths?: ReadonlyArray<string>;
    /** System Admin の申請資格または有効な権限があるユーザーが、現在選択中プロジェクトに依存せず利用するパス。 */
    systemAdminProductIndependentPaths?: ReadonlyArray<string>;
  };
}

const matchesCurrentPath = (paths: ReadonlyArray<string> | undefined): boolean => {
  if (!paths || paths.length === 0) return false;
  if (typeof window === 'undefined') return false;
  // 呼び出し元 (例: front-desk は router basepath `/desk` で動作) によっては実際の
  // window.location.pathname が `/desk/desktop-landing/` のようになるため、完全一致では落ちない。
  // publicPaths のエントリは必ず `/` 始まり想定なので、trimmed が `/` で始まる限り
  // `endsWith(trimmed)` 成立時点でセグメント境界は trimmed 先頭の `/` によって保証される。
  // 例: `/foo-desktop-landing`.endsWith('/desktop-landing') は false (`-desktop-landing` で区切り不一致)。
  const current = window.location.pathname.replace(/\/+$/, '');
  return paths.some((p) => {
    const trimmed = p.replace(/\/+$/, '');
    if (trimmed === '') return false;
    if (!trimmed.startsWith('/')) return current === trimmed;
    return current === trimmed || current.endsWith(trimmed);
  });
};

export const ProductWrapper: React.FC<PropsWithChildren<ProductWrapper.Props>> = ({
  productKey,
  navigation,
  queryClient,
  initialProjectId,
  publicPaths,
  systemAdminProductIndependentPaths,
  children,
}) => {
  const [defaultQueryClient] = useState(() => new QueryClient());
  const client = queryClient ? queryClient : defaultQueryClient;
  const isPublic = matchesCurrentPath(publicPaths);
  const isSystemAdminProductIndependent = matchesCurrentPath(systemAdminProductIndependentPaths);
  return (
    <QueryClientProvider client={client}>
      <MfaRequiredErrorRedirector queryClient={client} />
      <SsoRequiredErrorRedirector queryClient={client} />
      <SourProvider>
        <AuthProvider initialProjectId={initialProjectId} skipProjectBootstrap={isSystemAdminProductIndependent}>
          {isPublic ? (
            children
          ) : (
            <Authenticator productKey={productKey} initialProjectId={initialProjectId}>
              <ProjectSelector
                productKey={productKey}
                initialProjectId={initialProjectId}
                allowSystemAdminProductBypass={isSystemAdminProductIndependent}
              >
                <Wrapper>
                  <GlobalNaviV2 productKey={productKey} options={navigation.globalNaviV2} subHeader={navigation.subHeader} />
                  <Main>
                    <IpGuard allowSystemAdminBypass={isSystemAdminProductIndependent}>{children}</IpGuard>
                  </Main>
                </Wrapper>
              </ProjectSelector>
            </Authenticator>
          )}
        </AuthProvider>
      </SourProvider>
    </QueryClientProvider>
  );
};

const MfaRequiredErrorRedirector = ({ queryClient }: { queryClient: QueryClient }) => {
  useEffect(() => {
    const handleError = (error: unknown) => {
      if (isMfaRequiredError(error)) redirectToMfaEnrollment();
    };

    for (const query of queryClient.getQueryCache().getAll()) {
      if (query.state.status === 'error') handleError(query.state.error);
    }

    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.query.state.status !== 'error') return;
      handleError(event.query.state.error);
    });

    const unsubscribeMutationCache = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.mutation.state.status !== 'error') return;
      handleError(event.mutation.state.error);
    });

    return () => {
      unsubscribeQueryCache();
      unsubscribeMutationCache();
    };
  }, [queryClient]);

  return null;
};

const SsoRequiredErrorRedirector = ({ queryClient }: { queryClient: QueryClient }) => {
  useEffect(() => {
    const handleError = (error: unknown) => {
      redirectForSsoRequiredError({ error });
    };

    for (const query of queryClient.getQueryCache().getAll()) {
      if (query.state.status === 'error') handleError(query.state.error);
    }

    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.query.state.status !== 'error') return;
      handleError(event.query.state.error);
    });

    const unsubscribeMutationCache = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.mutation.state.status !== 'error') return;
      handleError(event.mutation.state.error);
    });

    return () => {
      unsubscribeQueryCache();
      unsubscribeMutationCache();
    };
  }, [queryClient]);

  return null;
};

const Skeleton = () => (
  <Wrapper>
    <div className="shrink-0 w-16 border-r border-r-muted bg-subtle-light" aria-hidden />
    <Main />
  </Wrapper>
);

const buildLoginPath = (params: { productKey: D.Object.Product.Key; initialProjectId?: string | undefined }): string => {
  if (typeof window === 'undefined') return route('/login');

  const currentUrl = new URL(`${window.location.pathname}${window.location.search}${window.location.hash}`, window.location.origin);
  const hasPid = !!currentUrl.searchParams.get('pid');

  if (params.initialProjectId || hasPid) {
    if (params.initialProjectId) currentUrl.searchParams.set('pid', params.initialProjectId);
    return buildLoginPathWithRedirect(`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
  }

  return buildLoginPathWithRedirect(route('/projects/to/latest', { query: { product: params.productKey } }));
};

const Authenticator = ({
  productKey,
  initialProjectId,
  children,
}: PropsWithChildren<{ productKey: D.Object.Product.Key; initialProjectId?: string | undefined }>) => {
  const { authState } = useAuthContext();

  if (AuthState.Is.Initial(authState)) {
    return <Skeleton />;
  }

  if (AuthState.Is.UnAuthenticated(authState)) {
    // ログイン画面へ
    window.location.href = buildLoginPath({ productKey, initialProjectId });
    return null;
  }

  return (
    <AccountProvider account={authState.authInfo.account}>
      <OrganizationProvider organizations={authState.authInfo.organizations}>{children}</OrganizationProvider>
    </AccountProvider>
  );
};

const ProjectSelector = ({
  productKey,
  initialProjectId,
  allowSystemAdminProductBypass,
  children,
}: PropsWithChildren<{
  productKey: D.Object.Product.Key;
  initialProjectId?: string | undefined;
  allowSystemAdminProductBypass?: boolean;
}>) => {
  const { latestProjectId, setLatestProjectId } = useLatestProjectId();
  const { setLatestOrganizationId } = useLatestOrganizationId();
  const [projectId, setProjectid] = useState(initialProjectId ?? latestProjectId);
  const { account } = useAccount();
  const { authState } = useAuthContext();
  const authInfo = AuthState.GetAuthInfo(authState);
  const requestedProjectContext = authInfo?.requestedProjectContext;

  const { organizations } = useOrganization();
  const projects = organizations.flatMap((organization) => organization.projects).flat();
  // bootstrap 経路で必要になる latestOrganizationId を、project から逆引きで設定する用のヘルパー。
  const findOrganizationIdByProject = useCallback(
    (pid: AuthInfo.Project['id']) =>
      organizations.find((o) => o.projects.some((p) => p.id === pid))?.id ??
      (requestedProjectContext?.project.id === pid ? requestedProjectContext.organization.id : undefined),
    [organizations, requestedProjectContext],
  );

  const switchProject = useCallback(
    ({ id }: { id: AuthInfo.Project['id'] }) => {
      if (projectId === id) return;
      setLatestProjectId(id);
      const orgId = findOrganizationIdByProject(id);
      if (orgId) setLatestOrganizationId(orgId);
      const product = D.Object.Product.Map[productKey];
      const url = `${product.path}?pid=${id}`;
      window.location.href = url;
      return;
    },
    [projectId, productKey, setLatestProjectId, setLatestOrganizationId, findOrganizationIdByProject],
  );

  // URL の `?pid=` 経由で project が確定する happy path では下の `if (!project)` 分岐に入らず
  // latestOrganizationId が同期されない。bootstrap (AuthProvider) の V1/V2 判定は URL の pid 基準に
  // なったが、旧 server (organizationId 必須) 互換のために organizationId も送っているので、
  // ここで毎レンダー組織IDを逆引きして同期する (server の全環境反映後は互換送信ごと削除できる)。
  useEffect(() => {
    if (!projectId) return;
    const orgId = findOrganizationIdByProject(projectId as AuthInfo.Project['id']);
    if (orgId) setLatestOrganizationId(orgId);
  }, [projectId, findOrganizationIdByProject, setLatestOrganizationId]);

  const isProductAccessibleForProject = (p: AuthInfo.Project): boolean =>
    D.Object.Project.isProductAccessible({ productSettings: p.productSettings, productKey, group: p.group, account });
  const isProductAccessibleForSelectedProject = (p: AuthInfo.SelectedProject): boolean =>
    p.membership
      ? D.Object.Project.isProductAccessible({
          productSettings: p.productSettings,
          productKey,
          group: p.membership.group,
          account,
        })
      : D.Object.Project.isProductEnabled({ productSettings: p.productSettings, productKey });
  const fallbackProductForSelectedProject = (p: AuthInfo.SelectedProject): D.Object.Product | 'no_products_in_use' => {
    if (p.membership) {
      return D.Object.Project.defaultProduct({
        project: p,
        group: p.membership.group,
        account,
      });
    }
    return (
      D.Object.Product.List.find((product) =>
        D.Object.Project.isProductEnabled({ productSettings: p.productSettings, productKey: product.key }),
      ) ?? 'no_products_in_use'
    );
  };
  const productEnabledProjects = projects.filter(isProductAccessibleForProject);
  const project = projectId && projects.find((project) => project.id === projectId);
  const matchingRequestedProjectContext =
    requestedProjectContext && requestedProjectContext.project.id === projectId ? requestedProjectContext : undefined;
  const requestedProjectContextProject = matchingRequestedProjectContext?.project;

  // 所属プロジェクトがない、または指定 projectId が所属外の場合はプロジェクト選択画面へ戻す。
  // 所属外 projectId で先頭プロジェクトへフォールバックすると、意図しないプロジェクトを開いてしまう。
  if ((projects.length === 0 && !requestedProjectContextProject) || (projectId && !project && !requestedProjectContextProject)) {
    window.location.href = route('/projects');
    return null;
  }

  const defaultProject = productEnabledProjects?.[0] || projects[0];

  if (!project && !requestedProjectContextProject) {
    setLatestProjectId(defaultProject.id);
    setProjectid(defaultProject.id);
    const orgId = findOrganizationIdByProject(defaultProject.id);
    if (orgId) setLatestOrganizationId(orgId);
  }

  const currentProject =
    (project && AuthInfo.ToSelectedProject(project)) ||
    requestedProjectContextProject ||
    (defaultProject && AuthInfo.ToSelectedProject(defaultProject));

  if (!currentProject) {
    window.location.href = route('/projects');
    return null;
  }
  const bypassProductAccess = allowSystemAdminProductBypass && (account.hasActiveSystemAdmin || account.isSystemAdminEligible);

  if (isMfaRequiredAndUnsatisfied(currentProject.authRequirements)) {
    redirectToMfaEnrollment();
    return null;
  }

  // プロダクト利用設定で無効、または権限グループでアクセス権がない場合の遷移先を、
  // このプロダクト内で解決する。
  // 旧 auth front (/auth/projects/$id) へ委ねると、旧ロジック (group 非考慮の isProductEnabled) で
  // 当プロダクトへ差し戻され、ProductWrapper の判定と無限にすれ違う（ループする）ため。
  if (!bypassProductAccess && !isProductAccessibleForSelectedProject(currentProject)) {
    const fallbackProduct = fallbackProductForSelectedProject(currentProject);
    // アクセス可能な別プロダクトがあれば、auth front を経由せず直接そのプロダクトへ遷移する
    if (fallbackProduct !== 'no_products_in_use' && fallbackProduct.key !== productKey) {
      window.location.href = `${fallbackProduct.path}?pid=${currentProject.id}`;
      return null;
    }
    // アクセスできるプロダクトが無い場合は auth へ飛ばさず、プロダクト内で権限なし画面を表示する
    return <NoAccessibleProduct />;

    // // プロダクト利用設定で無効、または権限グループでアクセス権がない場合は他アプリへリダイレクト
    // window.location.href = route('/projects/$id', { path: { id: currentProject.id } });
    // return null;
  }

  return (
    <ProjectProvider project={currentProject} switchProject={switchProject} projects={projects}>
      {children}
    </ProjectProvider>
  );
};

const IpGuard = ({
  children,
  allowSystemAdminBypass,
}: PropsWithChildren<{
  allowSystemAdminBypass?: boolean;
}>) => {
  const { account } = useAccount();
  if (allowSystemAdminBypass && (account.hasActiveSystemAdmin || account.isSystemAdminEligible)) return children;
  return <ProjectIpGuard>{children}</ProjectIpGuard>;
};
